"use client";

import { useUser } from "@clerk/nextjs";
import { Ticket } from "lucide-react";
import DashboardCard from "./DashboardCard";
import {
  getPassInfoFromMetadata,
  getPassDisplay,
} from "@/components/user-button-pages/helpers";
import { PASSES } from "@/types/pass";
import { cn } from "@/lib/utils";

/**
 * 대시보드 "감자 이용권" 컴팩트 위젯 (JCG-2073).
 * Clerk publicMetadata 에서 현재 이용권·상태·유효기간만 요약(추가 fetch 없음). free·체험 사용자는
 * 카드 헤더 링크를 업그레이드(/membership)로 돌려 작은 카드 폭을 유지한다.
 */
export default function DashboardPass() {
  const { user } = useUser();
  const passInfo = getPassInfoFromMetadata(user?.publicMetadata);
  const passName = PASSES[passInfo.passType]?.name ?? "Free";
  // 상태 라벨·무료 여부·유효기간은 공유 헬퍼로 파생 — 대시보드/이용권 탭이 일치하도록(코드리뷰 #1·#6).
  // free(무료)는 isActive=true·expiresAt=null이라 그냥 두면 "활성화 · 평생 이용"으로 오해되므로
  // 헬퍼가 "무료"로 표기하고, 아래에서 유효기간 대신 업그레이드 안내를 보여준다.
  const { stateLabel, isFree, expiry } = getPassDisplay(passInfo);
  const showUpgrade = passInfo.passType === "free" || passInfo.isTrial;

  return (
    <DashboardCard
      icon={Ticket}
      title="감자 이용권"
      href={showUpgrade ? "/membership" : "/my/pass"}
      linkLabel={showUpgrade ? "업그레이드" : "전체 보기"}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-lg font-extrabold text-foreground">
          {passName}
        </span>
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold",
            passInfo.isActive && !isFree
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              : "bg-muted text-muted-foreground",
          )}
        >
          {stateLabel}
        </span>
      </div>
      <p className="mt-1 truncate text-[11px] text-muted-foreground">
        {isFree
          ? "업그레이드하면 모든 기능 이용"
          : expiry === "평생"
            ? "평생 이용"
            : `${expiry}까지`}
      </p>
    </DashboardCard>
  );
}
